import React, { useState, useEffect, useRef } from "react";
import "./MyTeam.css";
import { getUserId } from "../../utils/tokenUtils";
import UserProfileBox from "../userProfileBox/UserProfileBox";
import { useNavigate } from "react-router-dom";
import { TeamMembersService } from "../../services/teamMembers/teamMembersService";
import Loader from "../../components/common/Loader/Loader";
import { useToast } from "../../components/common/ToastManager/ToastContext";
import TopBar from "../../components/TopBar/TopBar";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

// Map position to display value
const getDisplayPosition = (position: string) => {
  if (position === "Reporting Manager") {
    return "Content Workflow Manager";
  } else if (position === "Admin") {
    return "AI Workforce Sponsor";
  }
  return position;
};

interface Member {
  name: string;
  email: string;
  roleDescription: string;
  position: string;
  avatarUrl: string;
  dob: string;
  phone: string;
  userId?: string;
  adminUserId?: number | null; // Add this to store Admin User ID
}

interface ProfilePopupProps {
  member: Member;
  onClose: () => void;
}


const ProfilePopup: React.FC<ProfilePopupProps> = ({ member, onClose }) => {
  const navigate = useNavigate();
  const [firstName, lastName] = member.name.split(" ");
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userId] = useState(getUserId());
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await TeamMembersService.getUsers(userId);
        // await fetch(`${API_URL}/api/getUsers`, {
        //   method: "POST",
        //   headers: { "Content-Type": "application/json" },
        //   body: JSON.stringify({ userId }),
        // });

        // const data = await response.json();
        // ✅ FIX: Use lowercase property names
        setUserRole(response.data.user?.role || null);
      } catch (error) {
      }
    };

    fetchUserInfo();
  }, [userId]);

  return (
    <div className="vp-overlay" onClick={onClose}>
      <div className="vp-modal" onClick={(e) => e.stopPropagation()}>
        <button className="vp-close-btn" onClick={onClose} aria-label="Close">×</button>

        <div className="vp-header-band" />

        <div className="vp-body">
          <div className="vp-avatar">
            {firstName?.[0]?.toUpperCase()}{lastName?.[0]?.toUpperCase()}
          </div>

          <h2 className="vp-name">{member.name}</h2>
          <span className="vp-role-badge">{getDisplayPosition(member.roleDescription)}</span>

          <div className="vp-divider" />

          <div className="vp-info-list">
            <div className="vp-info-row">
              <span className="vp-info-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                </svg>
              </span>
              <div className="vp-info-text">
                <span className="vp-info-label">Email</span>
                <span className="vp-info-value">{member.email || "N/A"}</span>
              </div>
            </div>
                {/* {userRole === "Admin" && (
                  <div
                    className="edit-button-container"
                    style={{ textAlign: "center", marginTop: "20px" }}
                  >
                    <button
                      className="edit-button"
                      style={{ padding: "10px 20px" }}
                      onClick={() => {
                        const [firstName, lastName] = member.name.split(" ");
                        navigate("/editteammember", {
                          state: {
                            member: {
                              firstName,
                              lastName,
                              email: member.email,
                              phone: member.phone || "",
                              dob: member.dob || "",
                              position: member.position,
                              userId: member.userId || "",
                              adminUserId: member.adminUserId ?? null, // Pass adminUserId here
                            },
                          },
                        });
                      }}
                    >
                      Edit
                    </button>
                  </div>
                )} */}
        </div>{/* vp-info-list */}
        </div>{/* vp-body */}
      </div>{/* vp-modal */}
    </div>
  );
};

const MyTeam = () => {
  const [teamMembers, setTeamMembers] = useState<Member[]>([]);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState<string | null>(null);
  const { showToast } = useToast();
  const [showLoader, setShowLoader] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const handleAddMember = () => {
    navigate("/addteammember");
  };
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchUserAndTeam = async () => {
    try {
      setShowLoader(true);
      const userId = getUserId();
      const response = await TeamMembersService.getUsers(userId);

      // ✅ FIX: Use correct property names from DB
      const role = response.data.user?.role || null;
      const loginEmail = response.data.user?.login_email || null;

      setUserRole(role);

      if (!loginEmail) {
        return;
      }

      // ✅ Fetch team members
      const teamData = await TeamMembersService.getTeamMembers(loginEmail);

      const mappedMembers = teamData.data.teamMembers.map((member: any) => ({
        name: `${member.first_name} ${member.last_name}`, // ✅ Correct keys
        email: member.login_email,
        roleDescription: member.role,
        position: "Reporting Manager",
        avatarUrl: "https://example.com/avatar.jpg",
        userId: member.user_id,
        adminUserId: member.admin_user_id,
      }));

      setTeamMembers(mappedMembers);
      setShowLoader(false);
    } catch (error) {
      setShowLoader(false);
    }
  };

  const toggleMenu = (index: number) => {
    setOpenMenuIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleViewProfile = (member: Member) => {
    setSelectedMember(member);
    setOpenMenuIndex(null);
  };

  useEffect(() => {
    fetchUserAndTeam();
  }, []);

  const handleConfirmDelete = async () => {
    if (!selectedMember?.userId) return;

    try {
      setShowDeleteModal(false);
      setSelectedMember(null);
      setShowLoader(true);
      
      let user_id = selectedMember?.userId;
      await TeamMembersService.deleteTeamMember(user_id);
      
      // Remove from UI only after API success
      setTeamMembers(prevMembers =>
        prevMembers.filter(member => member.userId !== selectedMember.userId)
      );
      
      showToast("Team member deleted successfully!", "success");
      setShowLoader(false);
    } catch (error) {
      showToast("Failed to delete team member. Please try again.", "error");
      setShowLoader(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedMember(null);
  };

  const editProfile = (member: Member) => {
    if (!member.userId) return;

    const [firstName, lastName] = member.name.trim().split(/\s+/);

    navigate("/editteammember", {
      state: {
        member: {
          firstName,
          lastName,
          email: member.email,
          phone: member.phone || "",
          dob: member.dob || "",
          position: member.position,
          userId: member.userId || "",
          adminUserId: member.adminUserId ?? null,
        },
      },
    });
  };
  return (
    <div className="team-container">
      <TopBar />

      <div className="team-header-bar">
        <h2 className="team-title">My Team</h2>
        {userRole === "Admin" && (
          <div className="team-header-actions">
            {/* <div className="sort-dropdown">
              <button className="sort-button">
                <img src="/images/sortby.svg" alt="Sort Icon" className="sort-icon" />
                Sort by
                <img src="/images/downarrow.svg" alt="Down Arrow" className="sort-icon" />
              </button>
            </div> */}
            <div className="team-actions">
              <button className="add-member-button" onClick={handleAddMember}>
                + Add Member
              </button>
              <button
                className="manage-bots-button"
                onClick={() => navigate("/managebots")}
              >
                Manage Helens
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="team-grid">
        {showLoader && <Loader />}
        {teamMembers.map((member, index) => (
          <div className="team-card" key={index}>
            <div className="menu-container">
              <div className="menu-icon" onClick={() => toggleMenu(index)}>
                ⋮
              </div>
              {openMenuIndex === index && (
                <div className="menu-dropdown" ref={menuRef}>
                  <div
                    className="menu-item"
                    onClick={() => handleViewProfile(member)}
                  >
                    View Profile
                  </div>
                  {userRole?.toLowerCase() === "admin" && (
                    <>
                      <div
                        className="menu-item"
                        onClick={() => editProfile(member)}
                      >
                        Edit Profile
                      </div>
                      <div
                        className="menu-item"
                        onClick={() => {
                          setSelectedMember(member);
                          setShowDeleteModal(true);
                          setOpenMenuIndex(null);
                        }}
                      >
                        Delete Profile
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="team-avatar">
              {member.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)}
            </div>
            <div className="team-name">{member.name}</div>
            <div className="team-position">{getDisplayPosition(member.position)}</div>
          </div>
        ))}
      </div>

      {selectedMember && !showDeleteModal && (
        <ProfilePopup
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}
      {showDeleteModal && selectedMember && (
        <ConfirmModal
          title="Remove Team Member"
          message={`Are you sure you want to remove ${selectedMember.name} from your team?`}
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  );
};

export default MyTeam;
